import { Mp3Encoder } from '@breezystack/lamejs';

import { API_URL } from 'src/config';

// --- MIME TYPES ---
const mimeTypes: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
  txt: 'text/plain',
  json: 'application/json',
  mp4: 'video/mp4',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  zip: 'application/zip',
  rar: 'application/vnd.rar',
  '7z': 'application/x-7z-compressed',
  tar: 'application/x-tar',
  csv: 'text/csv',
};

export const getMimeType = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return mimeTypes[ext] || 'application/octet-stream';
};

// --- FETCH IMAGE BLOB ---
export const fetchImageBlob = async (
  fileUrl: string
): Promise<{ blob: Blob; contentType: string }> => {
  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }

    const contentType = response.headers.get('Content-Type') || 'application/octet-stream';
    const blob = await response.blob();
    return { blob, contentType };
  } catch (error) {
    console.error('Error fetching file:', error);
    throw error;
  }
};

// --- GET PRESIGNED URL ---
export const getPresignedUrl = async (
  fileName: string,
  fileType: string
): Promise<{ url: string; filePath: string }> => {
  try {
    const response = await fetch(`${API_URL}/api/storage/get-signed-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName, fileType }),
    });

    if (!response.ok) {
      throw new Error('Failed to get presigned URL');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting presigned URL:', error);
    throw error;
  }
};

// --- UPLOAD FILE TO S3 ---
export const uploadFile = async (presignedUrl: string, fileBlob: Blob) => {
  try {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': fileBlob.type,
      },
      body: fileBlob,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

// --- MAIN UPLOAD HANDLER ---
export const handleUpload = async (
  fileUrl: string,
  getSignedUrl: (params: {
    fileName: string;
    fileType: string;
  }) => Promise<{ data: { url: string; filePath: string } }>,
  getMediaUrl: (params: { fileName: any }) => Promise<any>
): Promise<string> => {
  try {
    const fileName = fileUrl.split('/').pop()?.split('?')[0] || 'uploaded-file';
    const contentType = getMimeType(fileName);

    const { blob } = await fetchImageBlob(fileUrl);

    const { data: presignedData } = await getSignedUrl({ fileName, fileType: contentType });

    await uploadFile(presignedData.url, blob);

    const mediaUrl = await getMediaUrl({ fileName: presignedData.filePath });

    return mediaUrl?.data ?? '';
  } catch (error) {
    console.error('❌ Upload process failed:', error);
    return '';
  }
};

export function extractParametersFromSendStructure(sendStructure: any) {
  const extracted: {
    header?: any[];
    body?: string[];
    buttons?: string[];
  } = {};

  // Check if sendStructure and components exist

  let components: any[] = [];
  if (Array.isArray(sendStructure)) {
    components = sendStructure;
  } else if (sendStructure?.components && Array.isArray(sendStructure.components)) {
    components = sendStructure.components;
  } else {
    return extracted;
  }

  // Extract header (media or text)
  const headerComponent = components.find((c: any) => c.type === 'header');
  if (headerComponent && headerComponent.parameters?.length > 0) {
    extracted.header = headerComponent.parameters
      .map((param: any) => {
        if (param.type === 'text') return param.text;
        if (param[param.type]) return param[param.type].link || '';
        return '';
      })
      .filter(Boolean); // remove empty string
  }

  // Extract body parameters (text values)
  const bodyComponent = components.find((c: any) => c.type === 'body');
  if (bodyComponent && bodyComponent.parameters?.length > 0) {
    extracted.body = bodyComponent.parameters.map((param: any) => param.text).filter(Boolean); // remove empty strings
  }

  // Extract buttons (quick_reply or copy_code)
  const buttonComponents = components.filter((c: any) => c.type === 'button');
  if (buttonComponents?.length) {
    extracted.buttons = [];
    buttonComponents.forEach((button: any) => {
      const param = button.parameters?.[0];
      if (button.sub_type === 'quick_reply' && param?.type === 'text') {
        extracted.buttons!.push(param.text);
      } else if (button.sub_type === 'copy_code' && param?.type === 'COUPON_CODE') {
        extracted.buttons!.push(param.coupon_code);
      }
    });
  }

  return extracted;
}

export function applyTemplateVariables(parameters: any, components: any) {
  let componentsArray: any[] = [];
  let isArrayInput = false;

  if (Array.isArray(components)) {
    componentsArray = components;
    isArrayInput = true;
  } else if (components?.components && Array.isArray(components.components)) {
    componentsArray = components.components;
  } else {
    return components;
  }

  const data = componentsArray.map((component: any) => {
    const type = component.type.toUpperCase();

    // HANDLE HEADER
    if (type === 'HEADER' && component.format?.toLowerCase() === 'text') {
      const headerText = component.text || '';
      const headerValue = parameters.header?.[0] || '';
      return {
        ...component,
        text: headerText.replace('{{1}}', headerValue),
      };
    }

    // HANDLE MEDIA HEADER — keep the file actually sent, never the template's example sample
    if (
      type === 'HEADER' &&
      ['image', 'video', 'document'].includes(component.format?.toLowerCase())
    ) {
      return {
        ...component,
        sentMediaUrl: parameters.header?.[0] || null,
      };
    }

    // HANDLE BODY
    if (type === 'BODY' && component.text) {
      let updatedText = component.text;
      parameters.body?.forEach((value: string, index: number) => {
        updatedText = updatedText.replace(`{{${index + 1}}}`, value);
      });

      return {
        ...component,
        text: updatedText,
      };
    }

    // HANDLE FOOTER
    if (type === 'FOOTER' && component.text) {
      const footerValue = parameters.footer?.[0] || component.text;
      return {
        ...component,
        text: footerValue,
      };
    }

    // HANDLE BUTTONS
    if (type === 'BUTTONS' && Array.isArray(component.buttons)) {
      const updatedButtons = component.buttons.map((btn: any, i: number) => {
        const btnType = btn.type?.toUpperCase();
        const paramValue = parameters.buttons?.[i];

        if (btnType === 'QUICK_REPLY' && paramValue) {
          return {
            ...btn,
            text: btn?.text,
          };
        }

        if (btnType === 'COPY_CODE' && paramValue) {
          return {
            ...btn,
            example: [paramValue],
          };
        }

        return btn;
      });

      return {
        ...component,
        buttons: updatedButtons,
      };
    }

    return component; // untouched
  });

  if (isArrayInput) {
    return data;
  }

  return {
    ...components,
    components: data,
  };
}

export function base64ToBlobURL(base64: string | undefined): string | null {
  if (!base64) {
    console.error('Invalid base64 input:', base64);
    return null;
  }

  try {
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
    const match = base64.match(/^data:(.*?);base64,/);
    const mimeType = match ? match[1] : '';
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error converting base64 to Blob:', error);
    return null;
  }
}
export function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function getFileSize(bytes: number, t: any) {
  if (bytes < 0) return 'Invalid size';
  const KB = bytes / 1024;
  if (KB < 1000) {
    return KB.toFixed(2) + `${t('KB')}`;
  }
  const MB = KB / 1024;
  return MB.toFixed(2) + `${t('MB')}`;
}

export function getAudioDuration(blobUrl: string): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.src = blobUrl;
    audio.preload = 'metadata';

    audio.onloadedmetadata = () => {
      if (isFinite(audio.duration)) {
        resolve(audio.duration);
      } else {
        audio.currentTime = Number.MAX_SAFE_INTEGER;
        audio.ontimeupdate = () => {
          resolve(audio.duration);
          audio.ontimeupdate = null;
        };
      }
    };

    audio.onerror = () => {
      reject(new Error('Failed to load audio metadata'));
    };
  });
}

export function base64ToBlob(base64: string): Blob | null {
  try {
    const [prefix, data] = base64.split(',');
    const mimeType = prefix.match(/^data:(.*?);base64/)?.[1] || '';
    const byteCharacters = atob(data);
    const byteArray = new Uint8Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([byteArray], { type: mimeType });
  } catch (error) {
    console.error('Error converting base64 to Blob:', error);
    return null;
  }
}

export function getBase64Info(base64String: string) {
  const mimeType = base64String.match(/^data:(.*?);base64,/)?.[1] || '';
  const contentLength = base64String.split(',')[1]?.length || 0;
  const fileSizeInBytes = (contentLength * 3) / 4 - (base64String.endsWith('=') ? 1 : 0);
  return { mimeType, fileSizeInBytes };
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / 1048576).toFixed(2)} MB`;
}

export async function uploadFileToGCS(
  file: File,
  signedUrl: string,
  mimetype: string
): Promise<void> {
  try {
    const response = await fetch(signedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': mimetype },
      body: file,
    });
    if (!response.ok) throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  } catch (error) {
    console.error('❌ Upload error:', error);
  }
}

export async function convertBlobToMp3(blob: Blob): Promise<Blob> {
  const arrayBuffer = await blob.arrayBuffer();
  const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
  const audioContext = new AudioContext();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  return audioBufferToMp3Blob(audioBuffer);
}

function audioBufferToMp3Blob(buffer: AudioBuffer): Blob {
  const channels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const kbps = 128;

  const mp3encoder = new Mp3Encoder(channels, sampleRate, kbps);
  const mp3Data: Uint8Array[] = [];
  const sampleBlockSize = 1152;

  if (channels === 1) {
    const samples = buffer.getChannelData(0);
    const sampleLength = samples.length;
    const int16Samples = new Int16Array(sampleLength);

    for (let i = 0; i < sampleLength; i++) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      int16Samples[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }

    for (let i = 0; i < sampleLength; i += sampleBlockSize) {
      const chunk = int16Samples.subarray(i, i + sampleBlockSize);
      const mp3buf = mp3encoder.encodeBuffer(chunk);
      if (mp3buf.length > 0) {
        mp3Data.push(mp3buf);
      }
    }
  } else {
    const left = buffer.getChannelData(0);
    const right = buffer.getChannelData(1);
    const sampleLength = left.length;

    const leftInt16 = new Int16Array(sampleLength);
    const rightInt16 = new Int16Array(sampleLength);

    for (let i = 0; i < sampleLength; i++) {
      const l = Math.max(-1, Math.min(1, left[i]));
      leftInt16[i] = l < 0 ? l * 0x8000 : l * 0x7fff;

      const r = Math.max(-1, Math.min(1, right[i]));
      rightInt16[i] = r < 0 ? r * 0x8000 : r * 0x7fff;
    }

    for (let i = 0; i < sampleLength; i += sampleBlockSize) {
      const leftChunk = leftInt16.subarray(i, i + sampleBlockSize);
      const rightChunk = rightInt16.subarray(i, i + sampleBlockSize);
      const mp3buf = mp3encoder.encodeBuffer(leftChunk, rightChunk);
      if (mp3buf.length > 0) {
        mp3Data.push(mp3buf);
      }
    }
  }

  const mp3buf = mp3encoder.flush();
  if (mp3buf.length > 0) {
    mp3Data.push(mp3buf);
  }

  const blobParts: BlobPart[] = mp3Data.map((chunk) => {
    const copy = new Uint8Array(chunk.byteLength);
    copy.set(chunk);
    return copy.buffer;
  });

  return new Blob(blobParts, { type: 'audio/mp3' });
}

export async function convertBlobToWav(blob: Blob): Promise<Blob> {
  const arrayBuffer = await blob.arrayBuffer();
  const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
  const audioContext = new AudioContext();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  return audioBufferToWavBlob(audioBuffer);
}

function audioBufferToWavBlob(buffer: AudioBuffer): Blob {
  const numOfChan = buffer.numberOfChannels;
  const length = buffer.length * numOfChan * 2 + 44;
  const bufferArray = new ArrayBuffer(length);
  const view = new DataView(bufferArray);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + buffer.length * 2 * numOfChan, true);
  writeString(view, 8, 'WAVE');

  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numOfChan, true);
  view.setUint32(24, buffer.sampleRate, true);
  view.setUint32(28, buffer.sampleRate * 2 * numOfChan, true); // Byte rate
  view.setUint16(32, numOfChan * 2, true); // Block align
  view.setUint16(34, 16, true); // Bits per sample

  writeString(view, 36, 'data');
  view.setUint32(40, buffer.length * numOfChan * 2, true);

  // Write PCM samples
  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let channel = 0; channel < numOfChan; channel++) {
      let sample = buffer.getChannelData(channel)[i];
      sample = Math.max(-1, Math.min(1, sample));
      sample = sample < 0 ? sample * 32768 : sample * 32767;
      view.setInt16(offset, sample, true);
      offset += 2;
    }
  }

  return new Blob([bufferArray], { type: 'audio/wav' });
}

function writeString(view: DataView, offset: number, string: string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}
